import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'compact' | 'horizontal';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-accent-300 transition-all"
      >
        <div className="sm:w-48 md:w-56 flex-shrink-0 overflow-hidden rounded-md aspect-video">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="flex flex-col justify-between flex-1 min-w-0">
          <div>
            <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-accent-100 text-accent-900 mb-2">
              {article.categoryName}
            </span>
            <h3 className="text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
              {article.title}
            </h3>
            <p className="text-sm text-foreground-600 mt-2 line-clamp-2 leading-relaxed">
              {article.summary}
            </p>
          </div>
          <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
            <span>{article.author}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300" />
            <span>{article.publishDate}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300" />
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {(article.views / 10000).toFixed(1)}万
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-start gap-3 py-3 border-b border-background-200/50 last:border-0"
      >
        <div className="w-20 h-14 flex-shrink-0 overflow-hidden rounded-md">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h4>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
            <span>{article.publishDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-accent-500" />
              {article.hotIndex}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  // default
  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-lg bg-background-50 border border-background-200/70 overflow-hidden hover:border-accent-300 transition-all"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      </div>
      <div className="p-4 md:p-5">
        <span className="inline-block px-2.5 py-1 rounded-md text-xs font-semibold bg-accent-500 text-background-50 mb-3">
          {article.categoryName}
        </span>
        <h3 className="text-base md:text-lg font-bold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 mt-2 line-clamp-2 leading-relaxed">
          {article.summary}
        </p>
        <div className="flex items-center justify-between mt-4 text-xs text-foreground-500">
          <div className="flex items-center gap-2">
            <span>{article.author}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300" />
            <span>{article.readTime}</span>
          </div>
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-accent-500" />
            {article.hotIndex}
          </span>
        </div>
      </div>
    </Link>
  );
}